import { LitElement, html, css } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import {
  JWT_KEY_LOCALSTORAGE,
  JWT_OTP_KEY_LOCALSTORAGE,
} from '../../environment/environment';
import { showNotification } from '../../LanghoutBetonApp';
import {
  unAuthenticatedAPICall,
  authenticatedAPICall,
  otpCall,
} from '../../client/api-client';
import { Capacitor } from '@capacitor/core';

import { copyIcon } from '../../elements/icons/icons';
import { SavePassword } from 'capacitor-ios-autofill-save-password';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

@customElement('login-element')
export class LoginForm extends LitElement {
  static styles = css`
    :host {
      font-size: 17px;
      color: var(--text-color-main);
    }

    #buttonWrapper {
      margin-top: 30px;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    #button {
      width: 100%;
      max-width: 300px;
      background-color: var(--primary-color);
      padding: 12px;
      color: var(--text-color-light);
      border-radius: 10px;
      font-weight: 900;
    }

    .row {
      width: 100%;
      margin-top: 30px;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
    }

    .row-wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }

    .label-wrapper {
      text-align: left;
      min-width: 100px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      font-weight: 900;
      padding-right: 10px;
    }

    input {
      padding: 7px;
      width: calc(100% - 18px);
      border-radius: 5px;
      font-size: 16px;
    }

    .copy-icon-wrapper {
      margin-left: 15px;
    }

    .copy-code-row:hover {
      cursor: pointer;
    }

    #otp-code {
      max-width: 150px;
      border: 0px;
      pointer-events: none;
    }

    langhout-button {
      width: 300px;
    }

    .button-margin {
      margin-top: 140px;
    }
  `;

  @query('#otp-code')
  public otpCodeField?: HTMLInputElement;

  @query('#otp-verification')
  public otpVerificationField?: HTMLInputElement;

  @property({ type: String, attribute: true })
  private otpCode = '';

  @property({ type: Number, attribute: true })
  private loginType = 0;

  constructor() {
    super();
    this.refreshTokenCall();
    this.refreshOTPTokenCall();
    document.addEventListener('keypress', this.keyPressListener);
  }

  private keyPressListener = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (this.loginType === 0) {
        this.login();
      }
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keypress', this.keyPressListener);
  }

  refreshTokenCall = async () => {
    const result = await authenticatedAPICall(
      '/token/refresh',
      'POST',
      new Headers(),
    );
    const body = result.body;
    if (isRecord(body) && typeof body.access_token === 'string') {
      localStorage.setItem(JWT_KEY_LOCALSTORAGE, body.access_token);
      window.location.href = '/';
    }
  };

  refreshOTPTokenCall = async () => {
    const result = await authenticatedAPICall(
      '/token/refresh/otp',
      'POST',
      new Headers(),
      null,
      JWT_OTP_KEY_LOCALSTORAGE,
    );
    const body = result.body;
    if (isRecord(body) && typeof body.access_token === 'string') {
      localStorage.setItem(JWT_OTP_KEY_LOCALSTORAGE, body.access_token);
      this.loginType = 1;
      setTimeout(() => {
        if (this.otpVerificationField) {
          this.otpVerificationField.focus();
        }
      }, 100);
    }
  };

  render() {
    if (this.loginType === 1) {
      document.dispatchEvent(
        new CustomEvent('hide-login', {
          detail: {},
          bubbles: true,
        }),
      );
      return html`
        ${this.renderOTPCode()}
        <div class="row">
          <div class="label-wrapper">Verification code:</div>
          <div class="input-wrapper">
            <input
              type="number"
              inputmode="numeric"
              pattern="[0-9]*"
              id="otp-verification"
              name="otp-verification"
              autocomplete="one-time-code"
              @keypress=${(e: KeyboardEvent) => {
                if (e.key === 'Enter') {
                  this.verifyOTP();
                } else {
                  setTimeout(() => {
                    const verificationCode = this.otpVerificationField
                      ? this.otpVerificationField.value
                      : '';
                    if (verificationCode.length >= 6) {
                      this.verifyOTP();
                    }
                  }, 10);
                }
              }}
            />
          </div>
        </div>
        <div class="row">
          <langhout-button
            text="Verify"
            @click="${(): void => {
              this.verifyOTP();
            }}"
          ></langhout-button>
        </div>
      `;
    }
    document.dispatchEvent(
      new CustomEvent('show-login', {
        detail: {},
        bubbles: true,
      }),
    );
    return html`
      <div class="row-wrapper">
        <div class="button-margin"></div>
        <div class="row">
          <div
            id="button"
            @click="${(): void => {
              this.login();
            }}"
          >
            Login
          </div>
        </div>
      </div>
    `;
  }

  renderOTPCode = () => {
    if (this.otpCode === '') {
      return html``;
    }
    return html`
      <div class="row">
        Copy the code and add it to the Google Authenticator App.
      </div>
      <div
        class="row copy-code-row"
        @click="${() => {
          if (!this.otpCodeField) {
            return;
          }
          this.otpCodeField.select();
          /* For mobile devices */
          this.otpCodeField.setSelectionRange(0, 99999);

          /* Copy the text inside the text field */
          navigator.clipboard.writeText(this.otpCodeField.value);
          /* Remove field focus */
          this.otpCodeField.blur();
          showNotification('Code copied to clipboard');
        }}"
      >
        <input type="text" id="otp-code" value="${this.otpCode}" />
        <div class="copy-icon-wrapper">${copyIcon()}</div>
      </div>
    `;
  };

  login = (): void => {
    const emailLogin = <HTMLInputElement>document.querySelector('#email-login');
    const email = emailLogin ? emailLogin.value : '';
    if (email === '') {
      showNotification('Please fill in your email.');
      return;
    }
    const passwordLogin = <HTMLInputElement>(
      document.querySelector('#password-login')
    );
    const password = passwordLogin ? passwordLogin.value : '';
    if (password === '') {
      showNotification('Please fill in your password.');
      return;
    }
    this.loginCall(email, password);
  };

  loginCall = async (email: string, password: string) => {
    const basicAuth = 'Basic ' + btoa(email + ':' + password);
    const headers = new Headers();
    headers.set('Authorization', basicAuth);
    const result = await unAuthenticatedAPICall(
      '/login/password',
      'GET',
      headers,
    );
    if (result) {
      const body = result.body;
      if (!isRecord(body) || typeof body.access_token !== 'string') {
        return;
      }
      localStorage.setItem(JWT_OTP_KEY_LOCALSTORAGE, body.access_token);
      if (!body.otp_set) {
        this.getOTP();
      }
      this.loginType = 1;
      setTimeout(() => {
        if (this.otpVerificationField) {
          this.otpVerificationField.focus();
        }
      }, 100);
      if (Capacitor.getPlatform() === 'ios') {
        await SavePassword.promptDialog({
          username: email,
          password: password,
        });
      }
    }
  };

  getOTP = async () => {
    const result = await otpCall('/login/otp', 'GET', new Headers());
    const body = result.body;
    if (isRecord(body) && typeof body.otp === 'string') {
      this.otpCode = body.otp;
    }
  };

  verifyOTP = async () => {
    if (!this.otpVerificationField) {
      return;
    }
    const verificationCode = this.otpVerificationField
      ? this.otpVerificationField.value
      : '';
    if (verificationCode === '') {
      showNotification('Please fill in your verification code.');
      return;
    }

    const result = await otpCall(
      '/login/otp',
      'POST',
      new Headers(),
      JSON.stringify({ code: verificationCode }),
    );
    if (this.otpVerificationField) {
      this.otpVerificationField.value = '';
    }
    const body = result.body;
    if (isRecord(body) && typeof body.access_token === 'string') {
      localStorage.setItem(JWT_KEY_LOCALSTORAGE, body.access_token);
      window.location.href = '/';
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'login-element': LoginForm;
  }
}
